/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text } from '@/shared/ui';
import type { PropertySuggestableField } from '@services/requests/generated/types.gen';
import { FieldValueAutocomplete } from './FieldValueAutocomplete';
import { useCondominiumPrefill, type CondominiumPrefillNotice } from './useCondominiumPrefill';
import type { PropertyFormValues } from './schema';

interface CondominiumFieldsProps {
  form: UseFormReturn<PropertyFormValues>;
  agencyId: string;
  propertyId?: string;
}

type TextKey = 'condominium_name' | 'tower_name' | 'development_name' | 'developer';

/** The condominium's text fields beside the name: building, development, developer. */
const SIBLING_FIELDS: ReadonlyArray<readonly [TextKey, PropertySuggestableField, string]> = [
  ['tower_name', 'tower_name', 'propertyDrawer.condominium.tower'],
  ['development_name', 'development_name', 'propertyDrawer.condominium.development'],
  ['developer', 'developer', 'propertyDrawer.condominium.developer'],
];

const TEXT_MAX = 120;

/**
 * Joins the filled groups as a sentence list in the current language.
 *
 * @param groups - Notice group keys.
 * @param t - Translator.
 * @param locale - Current language.
 * @returns "endereço, CEP e bairro".
 */
function listGroups(groups: string[], t: TFunction, locale: string): string {
  const labels = groups.map((g) => t(`propertyDrawer.prefill.groups.${g}`));
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(labels);
  } catch {
    return labels.join(', ');
  }
}

/**
 * The short notice of what the condominium prefill filled.
 *
 * @param props - The notice to show.
 * @returns The notice element.
 */
function PrefillNotice({ notice }: { notice: CondominiumPrefillNotice }) {
  const { t, i18n } = useTranslation('features/properties');
  return (
    <Text
      variant="meta"
      as="p"
      role="status"
      data-testid="condominium-prefill-notice"
      className="m-0 rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-secondary)] px-[var(--space-3)] py-[var(--space-2-5)] leading-[var(--leading-relaxed)] text-[var(--text-secondary)]"
    >
      {t('propertyDrawer.prefill.notice', {
        count: notice.matched,
        name: notice.condominium,
        fields: listGroups(notice.groups, t, i18n.language),
      })}
    </Text>
  );
}

/**
 * Condominium name (with prefill), building/tower, development and developer,
 * each suggesting the values the agency already uses.
 *
 * @param props - Form, agency and the property under edit.
 * @returns The condominium fields.
 */
export function CondominiumFields({ form, agencyId, propertyId }: CondominiumFieldsProps) {
  const { t } = useTranslation('features/properties');
  const prefill = useCondominiumPrefill(form, agencyId, propertyId);

  return (
    <div className="flex flex-col gap-(--space-3)" data-testid="condominium-fields">
      <Controller
        name="condominium_name"
        control={form.control}
        render={({ field }) => (
          <FieldValueAutocomplete
            agencyId={agencyId}
            field="condominium"
            label={t('propertyDrawer.condominium.name')}
            value={field.value}
            onChange={field.onChange}
            onPick={prefill.onPick}
            maxLength={TEXT_MAX}
          />
        )}
      />
      {prefill.notice && <PrefillNotice notice={prefill.notice} />}
      <div className="grid grid-cols-1 gap-(--space-3) sm:grid-cols-2">
        {SIBLING_FIELDS.map(([name, suggestable, labelKey]) => (
          <Controller
            key={name}
            name={name}
            control={form.control}
            render={({ field }) => (
              <FieldValueAutocomplete
                agencyId={agencyId}
                field={suggestable}
                label={t(labelKey)}
                value={field.value}
                onChange={field.onChange}
                maxLength={TEXT_MAX}
              />
            )}
          />
        ))}
      </div>
    </div>
  );
}
